/**
 * Asistente de rol (pestaña Roles de usuariosroles.js): crear un rol nuevo o
 * editar uno existente como unidad, sin escribir programas a mano.
 *
 * UI al estilo Bankivo: un acordeón por módulo con checkbox de módulo
 * completo y contador n/m, y dentro cada pantalla con su checkbox y solo los
 * flags de acción que esa pantalla consulta (catalogoProgramas.js). Al
 * marcar, programa/descripción/categoría salen del catálogo.
 *
 * Crear: "Copiar permisos de" precarga la selección desde otro rol.
 * Editar (prop rolEditar): precarga las filas del rol y guarda por diff —
 * upsert de lo marcado que cambió y eliminación de las filas del catálogo
 * que se desmarcaron. Las filas cuyo programa no está en el catálogo
 * (creadas por el escritorio ares) se muestran como informativas y NUNCA se
 * tocan en la edición; al crear copiando, se copian tal cual.
 *
 * El guardado reutiliza /api/config_rol_guardar y /api/config_rol_eliminar
 * fila por fila; la fase 4 del plan lo convertirá en un request transaccional.
 */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import { useGlobalMDAlert } from "context";
import { FP, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { getCatalogoModulos, getEntradaPrograma } from "lib/seguridad/catalogoProgramas";

const ETIQUETA_FLAG = {
  modificar: "Modificar",
  borrar: "Borrar",
  versucursal: "Ver sucursal",
  insertar: "Insertar",
};

const FLAGS = Object.keys(ETIQUETA_FLAG);

// Apps del sistema: el catálogo de préstamos (secciones de routes.js) y el
// de la app de Facturación POS (sección propia de routesfac.js). El selector
// solo filtra lo VISIBLE: la selección de la app oculta se conserva y se
// guarda igual.
const SECCION_FAC = "Facturación POS";
const APPS = [
  { clave: "prestamos", etiqueta: "Préstamos" },
  { clave: "facturacion", etiqueta: "Facturación POS" },
];
const appDeSeccion = (seccion) => (seccion === SECCION_FAC ? "facturacion" : "prestamos");

const flagsVacios = { modificar: "N", borrar: "N", versucursal: "N", insertar: "N" };

const flagsDeFila = (fila) => ({
  modificar: fila.modificar === "S" ? "S" : "N",
  borrar: fila.borrar === "S" ? "S" : "N",
  versucursal: fila.versucursal === "S" ? "S" : "N",
  insertar: fila.insertar === "S" ? "S" : "N",
});

const mismosFlags = (a, b) => FLAGS.every((f) => (a?.[f] || "N") === (b?.[f] || "N"));

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };

const seccionSx = {
  px: 1.5,
  py: 0.7,
  fontSize: "0.72rem",
  fontWeight: 800,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: FP.color.textMuted,
};

export default function AsistenteRol({
  open,
  onClose,
  rolesExistentes,
  filasRoles,
  rolEditar,
  onGuardado,
}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const esEdicion = Boolean(rolEditar);

  const [nombre, setNombre] = useState("");
  const [copiaDe, setCopiaDe] = useState("");
  // programa -> flags; la presencia de la clave equivale a "marcado".
  const [sel, setSel] = useState({});
  // Filas cuyo programa no está en el catálogo (escritorio ares). En edición
  // son informativas (no se tocan); al crear copiando se copian tal cual.
  const [extras, setExtras] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [progreso, setProgreso] = useState(null); // { hecho, total }
  // App cuyo catálogo se muestra; al editar/copiar se posiciona en la app
  // donde el rol tiene más pantallas.
  const [app, setApp] = useState("prestamos");

  const secciones = useMemo(() => {
    const grupos = [];
    getCatalogoModulos().forEach((m) => {
      const ultimo = grupos[grupos.length - 1];
      if (!ultimo || ultimo.seccion !== m.seccion)
        grupos.push({ seccion: m.seccion, modulos: [m] });
      else ultimo.modulos.push(m);
    });
    return grupos;
  }, []);

  const ordenCatalogo = useMemo(
    () => secciones.flatMap((s) => s.modulos.flatMap((m) => m.programas)),
    [secciones]
  );

  const seccionesVisibles = useMemo(
    () => secciones.filter((s) => appDeSeccion(s.seccion) === app),
    [secciones, app]
  );

  const programasVisibles = useMemo(
    () => seccionesVisibles.flatMap((s) => s.modulos.flatMap((m) => m.programas)),
    [seccionesVisibles]
  );

  // Pantallas marcadas por app, para los contadores del selector.
  const marcadosPorApp = useMemo(() => {
    const conteo = { prestamos: 0, facturacion: 0 };
    Object.keys(sel).forEach((programa) => {
      const entrada = getEntradaPrograma(programa);
      if (entrada) conteo[appDeSeccion(entrada.seccion)] += 1;
    });
    return conteo;
  }, [sel]);

  const appPredominante = (selMap) => {
    const conteo = { prestamos: 0, facturacion: 0 };
    Object.keys(selMap).forEach((programa) => {
      const entrada = getEntradaPrograma(programa);
      if (entrada) conteo[appDeSeccion(entrada.seccion)] += 1;
    });
    return conteo.facturacion > conteo.prestamos ? "facturacion" : "prestamos";
  };

  // Filas actuales del rol en edición: programa -> { id, flags }.
  const filasIniciales = useMemo(() => {
    if (!esEdicion) return {};
    const mapa = {};
    filasRoles
      .filter((f) => f.rol === rolEditar)
      .forEach((f) => {
        mapa[f.programa] = { id: f.id, flags: flagsDeFila(f) };
      });
    return mapa;
  }, [esEdicion, rolEditar, filasRoles]);

  const precargarDeRol = (rol) => {
    const nuevoSel = {};
    const nuevosExtras = [];
    filasRoles
      .filter((f) => f.rol === rol)
      .forEach((f) => {
        if (getEntradaPrograma(f.programa)) nuevoSel[f.programa] = flagsDeFila(f);
        else
          nuevosExtras.push({
            programa: f.programa,
            descripcion: f.descripcion || "",
            categoria: f.categoria || "",
            ...flagsDeFila(f),
            on: true,
          });
      });
    setSel(nuevoSel);
    setExtras(nuevosExtras);
    setApp(appPredominante(nuevoSel));
  };

  // Al abrir en modo edición, precargar el rol.
  useEffect(() => {
    if (open && esEdicion) {
      setNombre(rolEditar);
      setCopiaDe("");
      precargarDeRol(rolEditar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rolEditar]);

  const resetear = () => {
    setNombre("");
    setCopiaDe("");
    setSel({});
    setExtras([]);
    setProgreso(null);
    setApp("prestamos");
  };

  const cerrar = () => {
    if (guardando) return;
    resetear();
    onClose();
  };

  const togglePrograma = (programa) =>
    setSel((s) => {
      const nuevo = { ...s };
      if (nuevo[programa]) delete nuevo[programa];
      else nuevo[programa] = { ...flagsVacios };
      return nuevo;
    });

  const toggleFlag = (programa, flag) =>
    setSel((s) => {
      if (!s[programa]) return s;
      return {
        ...s,
        [programa]: { ...s[programa], [flag]: s[programa][flag] === "S" ? "N" : "S" },
      };
    });

  const marcarModulo = (modulo, marcar) =>
    setSel((s) => {
      const nuevo = { ...s };
      modulo.programas.forEach((p) => {
        if (marcar) nuevo[p.programa] = nuevo[p.programa] || { ...flagsVacios };
        else delete nuevo[p.programa];
      });
      return nuevo;
    });

  // Marca/desmarca solo la app visible: la selección de la otra app no se toca.
  const marcarTodo = (marcar) =>
    setSel((s) => {
      const nuevo = { ...s };
      programasVisibles.forEach((p) => {
        if (marcar) nuevo[p.programa] = nuevo[p.programa] || { ...flagsVacios };
        else delete nuevo[p.programa];
      });
      return nuevo;
    });

  const aplicarCopia = (rol) => {
    setCopiaDe(rol);
    if (!rol) {
      setSel({});
      setExtras([]);
      setApp("prestamos");
      return;
    }
    precargarDeRol(rol);
  };

  const totalMarcados = Object.keys(sel).length + extras.filter((x) => x.on).length;

  const guardar = async () => {
    const nombreRol = nombre.trim();
    if (!nombreRol) {
      showAlert({ message: "El nombre del rol no puede estar en blanco", color: "warning" });
      return;
    }
    if (!esEdicion && rolesExistentes.some((r) => r.toLowerCase() === nombreRol.toLowerCase())) {
      showAlert({
        message: "Ya existe un rol con ese nombre; use otro o edítelo desde el listado",
        color: "warning",
      });
      return;
    }

    // Altas y cambios: lo marcado que no existía o cuyos flags cambiaron.
    const altas = ordenCatalogo
      .filter((p) => sel[p.programa])
      .filter(
        (p) =>
          !filasIniciales[p.programa] ||
          !mismosFlags(filasIniciales[p.programa].flags, sel[p.programa])
      )
      .map((p) => ({
        etiqueta: p.etiqueta,
        cuerpo: {
          programa: p.programa,
          descripcion: p.etiqueta,
          categoria: p.categoria,
          ...sel[p.programa],
        },
      }));
    if (!esEdicion) {
      extras
        .filter((x) => x.on)
        .forEach((x) =>
          altas.push({
            etiqueta: x.descripcion || x.programa,
            cuerpo: {
              programa: x.programa,
              descripcion: x.descripcion,
              categoria: x.categoria,
              ...flagsDeFila(x),
            },
          })
        );
    }

    // Bajas (solo edición): filas del catálogo que se desmarcaron. Las filas
    // fuera del catálogo jamás se eliminan desde aquí.
    const eliminar = esEdicion
      ? Object.entries(filasIniciales)
          .filter(([programa]) => getEntradaPrograma(programa) && !sel[programa])
          .map(([programa, { id }]) => ({
            etiqueta: getEntradaPrograma(programa).etiqueta,
            id,
          }))
      : [];

    if (!esEdicion && !altas.length) {
      showAlert({ message: "Marque al menos una pantalla para el rol", color: "warning" });
      return;
    }
    if (esEdicion && !altas.length && !eliminar.length) {
      showAlert({ message: "No hay cambios que guardar", color: "info" });
      return;
    }
    if (esEdicion && !Object.keys(sel).length && !extras.length) {
      showAlert({
        message: "Un rol no puede quedar sin pantallas; use Eliminar rol en el listado",
        color: "warning",
      });
      return;
    }

    setGuardando(true);
    const errores = [];
    const total = altas.length + eliminar.length;
    let hecho = 0;
    try {
      for (let i = 0; i < altas.length; i += 1) {
        hecho += 1;
        setProgreso({ hecho, total });
        try {
          // eslint-disable-next-line no-await-in-loop
          const { ok, data } = await post("/api/config_rol_guardar", {
            rol: nombreRol,
            ...altas[i].cuerpo,
          });
          if (!ok) errores.push(`${altas[i].etiqueta} (${data.Error || "error"})`);
        } catch (e) {
          errores.push(altas[i].etiqueta);
        }
      }
      for (let i = 0; i < eliminar.length; i += 1) {
        hecho += 1;
        setProgreso({ hecho, total });
        try {
          // eslint-disable-next-line no-await-in-loop
          const { ok, data } = await post("/api/config_rol_eliminar", { id: eliminar[i].id });
          if (!ok) errores.push(`${eliminar[i].etiqueta} (${data.Error || "error"})`);
        } catch (e) {
          errores.push(eliminar[i].etiqueta);
        }
      }
    } finally {
      setGuardando(false);
      setProgreso(null);
    }

    if (errores.length) {
      // Los cambios se aplican fila por fila (upsert): volver a guardar la
      // misma selección completa lo que faltó sin duplicar nada.
      showAlert({
        message: `Cambios incompletos; fallaron: ${errores.join(
          ", "
        )}. Vuelva a guardar para reintentar.`,
        color: "error",
      });
      onGuardado(nombreRol);
      return;
    }
    showAlert({
      message: esEdicion
        ? `Rol ${nombreRol} actualizado`
        : `Rol ${nombreRol} creado con ${altas.length} pantallas`,
      color: "success",
    });
    onGuardado(nombreRol);
    resetear();
    onClose();
  };

  const filaPrograma = (p) => {
    const marcado = Boolean(sel[p.programa]);
    return (
      <Stack
        key={p.programa}
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        sx={{
          pl: 1.5,
          pr: 1,
          py: 0.3,
          borderRadius: 1.5,
          "&:hover": { bgcolor: FP.color.surfaceAlt },
        }}
      >
        <Checkbox
          size="small"
          checked={marcado}
          onChange={() => togglePrograma(p.programa)}
          inputProps={{ "aria-label": `${p.modulo}: ${p.etiqueta}` }}
          sx={{ p: 0.6 }}
        />
        <Box
          sx={{ flex: 1, minWidth: 180, cursor: "pointer" }}
          onClick={() => togglePrograma(p.programa)}
        >
          {/* Solo la etiqueta humana: el usuario no conoce los códigos ares. */}
          <Typography
            sx={{
              fontSize: "0.88rem",
              fontWeight: marcado ? 700 : 500,
              color: FP.color.textPrimary,
            }}
          >
            {p.etiqueta}
          </Typography>
        </Box>
        {p.acciones.length > 0 && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {p.acciones.map((flag) => (
              <Stack key={flag} direction="row" alignItems="center">
                <Checkbox
                  size="small"
                  disabled={!marcado}
                  checked={marcado && sel[p.programa][flag] === "S"}
                  onChange={() => toggleFlag(p.programa, flag)}
                  inputProps={{
                    "aria-label": `${p.modulo}: ${p.etiqueta}: ${ETIQUETA_FLAG[flag]}`,
                  }}
                  sx={{ p: 0.4 }}
                />
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: marcado ? FP.color.textSecondary : FP.color.textMuted,
                  }}
                >
                  {ETIQUETA_FLAG[flag]}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    );
  };

  const acordeonModulo = (m) => {
    const marcados = m.programas.filter((p) => sel[p.programa]).length;
    const total = m.programas.length;
    const todo = marcados === total;
    let colorChip = "default";
    if (todo) colorChip = "success";
    else if (marcados > 0) colorChip = "warning";
    return (
      <Accordion
        key={m.moduloKey}
        disableGutters
        sx={{
          mb: 1,
          boxShadow: "none",
          border: `1px solid ${FP.color.border}`,
          borderRadius: "10px !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ "&:hover": { bgcolor: FP.color.surfaceAlt }, borderRadius: 2.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, mr: 1 }}>
            <Checkbox
              size="small"
              checked={todo}
              indeterminate={marcados > 0 && !todo}
              onChange={(e) => marcarModulo(m, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              inputProps={{ "aria-label": `Marcar módulo ${m.modulo}` }}
              sx={{ p: 0.5 }}
            />
            <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: FP.color.textPrimary }}>
              {m.modulo}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip
              label={`${marcados}/${total}`}
              size="small"
              variant="outlined"
              color={colorChip}
              sx={{ fontWeight: 700 }}
            />
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 1, bgcolor: FP.color.surface }}>
          {m.programas.map(filaPrograma)}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={cerrar}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {esEdicion ? `Editar rol: ${rolEditar}` : "Nuevo rol"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0, mb: 1 }}>
          <Grid item xs={12} sm={esEdicion ? 6 : 4}>
            <TextField
              fullWidth
              label="Nombre del rol"
              value={nombre}
              disabled={esEdicion}
              onChange={(e) => setNombre(e.target.value)}
              sx={fpInputSx}
            />
          </Grid>
          {!esEdicion && (
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Copiar permisos de"
                value={copiaDe}
                onChange={(e) => aplicarCopia(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="">Empezar en blanco</MenuItem>
                {rolesExistentes.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={esEdicion ? 6 : 4}
            sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => marcarTodo(true)}
              sx={{ ...btnBase, color: FP.color.navy, borderColor: FP.color.navy }}
            >
              Seleccionar todo
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => marcarTodo(false)}
              sx={{ ...btnBase, color: FP.color.textSecondary, borderColor: FP.color.border }}
            >
              Deseleccionar todo
            </Button>
          </Grid>
        </Grid>

        {/* Selector de app: filtra lo visible; la selección de la otra app
            se conserva (los contadores lo recuerdan) y se guarda igual. */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={app}
          onChange={(e, valor) => valor && setApp(valor)}
          sx={{ mb: 1.5 }}
        >
          {APPS.map((a) => (
            <ToggleButton
              key={a.clave}
              value={a.clave}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                px: 1.6,
                "&.Mui-selected": {
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  "&:hover": { bgcolor: FP.color.navy },
                },
              }}
            >
              {a.etiqueta}
              {marcadosPorApp[a.clave] > 0 ? ` (${marcadosPorApp[a.clave]})` : ""}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {seccionesVisibles.map((s) => (
          <Box key={s.seccion} sx={{ mb: 1.5 }}>
            <Typography sx={seccionSx}>{s.seccion}</Typography>
            {s.modulos.map(acordeonModulo)}
          </Box>
        ))}

        {extras.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={seccionSx}>
              {esEdicion
                ? "Otros programas (del escritorio)"
                : `Otros programas (copiados de ${copiaDe})`}
            </Typography>
            <Box
              sx={{
                border: `1px dashed ${FP.color.border}`,
                borderRadius: 2.5,
                px: 1,
                py: 0.5,
              }}
            >
              {extras.map((x, idx) => (
                <Stack key={x.programa} direction="row" alignItems="center" sx={{ py: 0.3 }}>
                  {esEdicion ? (
                    <Checkbox size="small" checked disabled sx={{ p: 0.6 }} />
                  ) : (
                    <Checkbox
                      size="small"
                      checked={x.on}
                      onChange={() =>
                        setExtras((xs) => xs.map((y, i) => (i === idx ? { ...y, on: !y.on } : y)))
                      }
                      inputProps={{ "aria-label": x.descripcion || x.programa }}
                      sx={{ p: 0.6 }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
                      {x.descripcion || x.programa}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                      {x.programa}
                      {FLAGS.filter((f) => x[f] === "S")
                        .map((f) => ` · ${ETIQUETA_FLAG[f]}`)
                        .join("")}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Box>
            {esEdicion && (
              <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted, mt: 0.5, px: 1.5 }}>
                Estas filas las administra el sistema de escritorio y no se modifican desde aquí.
              </Typography>
            )}
          </Box>
        )}

        {guardando && progreso && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, mb: 0.5 }}>
              Aplicando cambios {progreso.hecho} de {progreso.total}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progreso.hecho / progreso.total) * 100}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: FP.color.textSecondary }}>
          {totalMarcados} pantallas marcadas
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={cerrar}
            disabled={guardando}
            sx={{ ...btnBase, color: FP.color.textSecondary }}
          >
            Cancelar
          </Button>
          <Button
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardar}
            disabled={guardando}
            sx={{
              ...btnBase,
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
              "&.Mui-disabled": { bgcolor: `${FP.color.navy}66`, color: "#FFFFFF" },
            }}
          >
            {esEdicion ? "Guardar cambios" : "Crear rol"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

AsistenteRol.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rolesExistentes: PropTypes.arrayOf(PropTypes.string).isRequired,
  filasRoles: PropTypes.arrayOf(PropTypes.object).isRequired,
  rolEditar: PropTypes.string,
  onGuardado: PropTypes.func.isRequired,
};

AsistenteRol.defaultProps = {
  rolEditar: null,
};
